/* iceberg-ui */
import { Badge, Flex, WithContext } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Paper, Tab } from '@mui/material';

/*  Shared  */
import { AppHeader } from 'application/Shared/Components/Headers/AppHeader';
import { getLicenseDataStatus } from 'application/Shared/Components/LicenseUsage/functions';
import { LicenseDataStatus } from 'application/Shared/Components/LicenseUsage/types';
import { MUITabsWrapper } from 'application/Shared/Components/MUITabsWrapper';
import {
    ConditionalTenantAppProvider,
    TenantAppContext,
    useTenantAppContext
} from 'application/Shared/Contexts/TenantAppContext';
import { Notify, Route, SessionHelper } from 'application/Shared/Helpers';
// TODO: Fix dependency cycle - SAAS-992
// eslint-disable-next-line import/no-cycle
import Notes from 'application/Shared/Pages/Notes/Containers/Page';
import { AdminView } from 'application/Shared/Views';

/*  NPM  */
import { useFetchTenantSetting } from 'hooks/dataAccess/Tenant/tenantSetting';
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

/*  Pages  */
import { AppDetails } from './Pages/AppDetails';
import { AppLogs } from './Pages/AppLogs';
import { ContractManagement } from './Pages/Pages/ContractManagement';
import { IntegrationUsersPage } from './Pages/Pages/IntegrationUsersPage';
import { SaaSAppUsersPage } from './Pages/Pages/SaasAppUsersPage';
import {
    SaasOpsApplicationContextProvider,
    useSaasOpsApplicationContext
} from './SaasOpsApplicationContext';

export const AdminApplication = ({ id }: { id?: string }) => {
    const [notify, setNotify] = useState(true);

    const { applicationId: applicationIdParam } = useParams();
    const applicationId = id || applicationIdParam;

    const params = useMemo(
        () => ({
            applicationId,
            includeActiveUser: true,
            includeAllUncategorized: true,
            includeApplication: true,
            includeApplicationLinkTags: true,
            includeContracts: true,
            includeManagementStage: true,
            includeRemoved: true,
            includeSource: true,
            includeTenantLicenseData: true,
            includeUncataloged: true,
            includeUncategorizedWorkLogins: true,
            includeWorkato: true,
            size: 1
        }),
        [applicationId]
    );

    return (
        <ConditionalTenantAppProvider
            applicationId={applicationId!}
            params={params}
        >
            <WithContext context={TenantAppContext}>
                {({ loading, tenantApp }) =>
                    loading && !tenantApp ? null : (
                        <SaasOpsApplicationContextProvider
                            tenantApp={tenantApp!}
                        >
                            <AdminView page="application">
                                <TenantAppView />
                                <LicenseNotification
                                    notify={notify}
                                    setNotify={setNotify}
                                />
                            </AdminView>
                        </SaasOpsApplicationContextProvider>
                    )
                }
            </WithContext>
        </ConditionalTenantAppProvider>
    );
};

const AdminAppDashboardHeader = ({
    setRerender
}: {
    setRerender: () => void;
}) => (
    <Paper
        elevation={0}
        sx={{
            borderRadius: 0,
            marginBottom: '24px',
            marginLeft: '-16px',
            marginRight: '-16px',
            marginTop: '-16px',
            paddingX: '24px',
            paddingY: '16px'
        }}
    >
        <AppHeader setRerender={setRerender} />
    </Paper>
);

const TabLabel = ({
    text,
    showBadge
}: {
    text: string;
    showBadge?: boolean;
}) => {
    const { t } = useTranslation();

    if (showBadge) {
        return (
            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
                {text}
                <Badge variant="primary" text={t('Common.new')} />
            </Flex>
        );
    }

    return <Flex style={{ alignItems: 'center' }}>{text}</Flex>;
};

const ApplicationTabs = () => {
    const { t } = useTranslation();
    const { applicationId, clientId, partnerId, tab } = useParams();
    const activeTab = tab ?? 'details';
    const { isLicenseDataAvailable } = useSaasOpsApplicationContext();
    const { setting: applicationLogging } = useFetchTenantSetting({
        clientId,
        key: 'security_logging'
    });

    const session = useSession();
    const sessionHelper = new SessionHelper(session);

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    const getTabProps = (path: string) => ({
        component: Link,
        to: `${baseAdminRoute}/application/${applicationId}/${path}`
    });

    const showLogsTab =
        applicationLogging && sessionHelper.hasPermission('application_logs');

    const tabs: Array<{
        id: string;
        label: string;
        show: boolean;
        showBadge?: boolean;
    }> = [
        {
            id: 'details',
            label: t('Common.applicationDetails'),
            show: true
        },
        {
            id: 'users',
            label: t('Common.users'),
            show: true
        },
        {
            id: 'licenses',
            label: t('Common.licenses'),
            show: isLicenseDataAvailable
        },
        {
            id: 'contract',
            label: t('Common.contractManagement'),
            show: true
        },
        {
            id: 'logs',
            label: t('Common.security_logs'),
            show: showLogsTab
        },
        {
            id: 'notes',
            label: t('Common.notes_and_recommendations'),
            show: true
        }
    ];

    return (
        <MUITabsWrapper value={activeTab}>
            {tabs.map(
                ({ id, label, show, showBadge }) =>
                    show && (
                        <Tab
                            key={id}
                            {...getTabProps(id)}
                            data-trackid={`applicationOverview_${id}`}
                            value={id}
                            label={
                                <TabLabel text={label} showBadge={showBadge} />
                            }
                            aria-label={`application ${id}`}
                        />
                    )
            )}
        </MUITabsWrapper>
    );
};

const TenantAppView = () => {
    const { tab } = useParams();
    const activeTab = tab ?? 'details';

    const { refetchTenantApps, tenantApp } = useTenantAppContext();

    const id = tenantApp?.application?.id;

    const handleUpdate = () => {
        refetchTenantApps();
    };

    return (
        <>
            <AdminAppDashboardHeader setRerender={handleUpdate} />
            {!!tenantApp?.application && (
                <>
                    <ApplicationTabs />
                    {activeTab === 'details' && (
                        <AppDetails
                            application={tenantApp?.application}
                            reloadApplication={handleUpdate}
                            tenantApp={tenantApp}
                        />
                    )}

                    {activeTab === 'users' && (
                        <SaaSAppUsersPage
                            tenantApp={tenantApp!}
                            reloadApplication={handleUpdate}
                        />
                    )}

                    {activeTab === 'licenses' && (
                        <IntegrationUsersPage tenantApp={tenantApp!} />
                    )}

                    {activeTab === 'contract' && (
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{ padding: '24px' }}
                        >
                            <ContractManagement
                                reloadParentFn={() => handleUpdate()}
                                applicationId={tenantApp?.applicationId}
                                managedByPartner={tenantApp?.managedByPartner}
                            />
                        </Paper>
                    )}

                    {activeTab === 'logs' && <AppLogs applicationId={id} />}
                    {activeTab === 'notes' && (
                        <Notes application={tenantApp?.application} noCards />
                    )}
                </>
            )}
        </>
    );
};

const LicenseNotification = ({
    notify,
    setNotify
}: {
    notify: boolean;
    setNotify: Dispatch<SetStateAction<boolean>>;
}) => {
    const { t } = useTranslation();
    const { tenantApp } = useTenantAppContext();
    const { isLicenseDataAvailable } = useSaasOpsApplicationContext();

    useEffect(() => {
        if (!notify || !isLicenseDataAvailable || !tenantApp) {
            return;
        }

        const licenseDataStatus = getLicenseDataStatus(tenantApp);
        if (licenseDataStatus === LicenseDataStatus.syncFailed) {
            Notify.e(t('LicenseData.status.syncFailed'));
        }

        setNotify(false);
    }, [notify, isLicenseDataAvailable, tenantApp, t, setNotify]);

    return null;
};
